import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Slider,
  LinearProgress,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SaveIcon from "@mui/icons-material/Save";
import EditNoteIcon from "@mui/icons-material/EditNote";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import HistoryIcon from "@mui/icons-material/History";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import HeaderBannerPage from "../components/HeaderBannerPage";
import { fetchPmIwoList } from "../actions/ProductionMonitorAction";
import {
  fetchErUnits,
  fetchErUnitFabrications,
  fetchErDelayReasons,
  fetchErDailyRemarks,
  saveErDailyRemark,
} from "../actions/ErectionMonitorAction";

/* -------------------- Erection -> Update Daily Remark --------------------
   1. IWO   (PreFabMonitor/GetIwoList)
   2. Unit  (ErecMonitor/GetUnits)
   3. Stage (ErecMonitor/GetUnitFabrications?iwoNo=&unitId= -> Fabrications, ProgressId)
   Remarks of the stage: GetDailyRemarks?progressId=
   Save: no remark for that date -> InsertDailyRemark
         remark already there for that date -> UpdateDailyRemark (changed fields only)
   Same design as the Pre Fabrication daily remark.
--------------------------------------------------------------------------- */

const NAVY = "#142c54";
const BLUE = "#154c79";

// Local yyyy-MM-dd (toISOString shifts to UTC and can give the previous day in Sri Lanka)
const formatDate = (dateObj) => {
  const d = new Date(dateObj);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const toYmd = (v) => String(v || "").slice(0, 10);

// typed digits -> yyyy-MM-dd
const formatTypedDate = (text) => {
  const digits = String(text || "").replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 4) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 4)}-${digits.slice(4)}`;
  return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6)}`;
};

const isValidYmd = (v) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v || "")) return false;
  const [y, m, d] = v.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
};

const toNumber = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

// number from the API -> text for the box ("" when nothing / 0)
const hoursText = (v) => (v === null || v === undefined || Number(v) === 0 ? "" : String(Number(v)));

const pct = (n) => `${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}%`;

const isStageDone = (s) => !!s && (s.CompletionStatus === true || Number(s.PhysicalCompletion) >= 100);

/* ---- Target date alert (same colours as Pre Fabrication) ----
   day before the target date -> yellow, on / after it -> red (nothing for completed stages) */
const daysBetween = (fromYmd, toYmdStr) => {
  const [y1, m1, d1] = fromYmd.split("-").map(Number);
  const [y2, m2, d2] = toYmdStr.split("-").map(Number);
  return Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 86400000);
};
const targetStatus = (s) => {
  const end = toYmd(s?.TargetDate);
  if (!s || isStageDone(s) || !/^\d{4}-\d{2}-\d{2}$/.test(end)) return null;
  const left = daysBetween(formatDate(new Date()), end);
  if (left === 1) return { level: "warn", title: "Target tomorrow", label: "Target tomorrow", text: `Target date of this stage is tomorrow (${end})` };
  if (left === 0) return { level: "due", title: "Target today", label: "Target today", text: `Target date of this stage is today (${end})` };
  if (left < 0) {
    const n = -left;
    return { level: "due", title: "Overdue", label: `Overdue ${n}d`, text: `Target date ${end} passed ${n} day${n > 1 ? "s" : ""} ago` };
  }
  return null;
};
const END_COLORS = {
  warn: { bg: "#fff4cc", border: "#f5b400", text: "#7a5600", chip: "#f5b400", chipText: "#3d2b00" },
  due: { bg: "#ffe5e5", border: "#d32f2f", text: "#8e1414", chip: "#d32f2f", chipText: "#fff" },
};

const cardSx = {
  p: { xs: 1.5, sm: 2.5 },
  borderRadius: "16px",
  border: "1px solid #d6dce5",
  backgroundColor: "#fff",
  boxShadow: "0px 4px 12px rgba(0,0,0,0.06)",
};

const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: "12px", backgroundColor: "#fff" } };

const InfoItem = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ fontSize: "11px", color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#0E3C66", wordBreak: "break-word" }}>
      {value || "—"}
    </Typography>
  </Box>
);

/* ---- CDL reason groups (common names, same as Pre Fabrication) ---- */
const CDL_REASON_GROUPS = {
  SF: "SWC Workshop Delay", // Flat bar blending
  SC: "SWC Workshop Delay", // Chamfer Cutting
  SB: "SWC Workshop Delay", // Bevel Cutting
  ST: "SWC Workshop Delay", // Transport Delay
  SM: "SWC Workshop Delay", // Material Delay
  SL: "SWC Workshop Delay", // Line heating delay
  IP: "Inspection Delay", // Production
  IQ: "Inspection Delay", // QC
  IO: "Inspection Delay", // ShipOwner
};
const cdlGroupOf = (code) => CDL_REASON_GROUPS[code] || "";

// Dropdown list: grouped reasons are hidden under their common name until it is clicked
const buildReasonOptions = (reasons, groupOf, expanded) => {
  const out = [];
  const added = {};
  (reasons || []).forEach((r) => {
    const g = groupOf ? groupOf(r.Code) : "";
    if (!g) {
      out.push(r);
      return;
    }
    if (added[g]) return;
    added[g] = true;
    const members = reasons.filter((x) => groupOf(x.Code) === g);
    out.push({ isGroup: true, Code: `__group_${g}`, Name: g, count: members.length });
    if (expanded[g]) members.forEach((m) => out.push({ ...m, inGroup: g }));
  });
  return out;
};

/* ---- Reason dropdown (short list, groups open on click) ---- */
const ReasonSelect = ({ title, reasons, reason, onReason, loading, groupOf, changed }) => {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [inputValue, setInputValue] = useState("");
  const keepOpen = useRef(false);

  const selected = (reasons || []).find((r) => r.Code === reason) || null;
  const selectedGroup = selected && groupOf ? groupOf(selected.Code) : "";
  const searching = !!inputValue.trim() && (!selected || inputValue !== selected.Name);
  const options = buildReasonOptions(reasons, groupOf, expanded);

  // typing searches every reason (names, codes and group names), no headers
  const filterOptions = (opts, { inputValue: text }) => {
    const q = text.trim().toLowerCase();
    if (!q || (selected && text === selected.Name)) return opts;
    return (reasons || [])
      .filter((o) => [o.Name, o.Code, groupOf ? groupOf(o.Code) : ""].some((v) => String(v || "").toLowerCase().includes(q)))
      .map((o) => (groupOf && groupOf(o.Code) ? { ...o, inGroup: groupOf(o.Code) } : o));
  };

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => {
        if (keepOpen.current) {
          keepOpen.current = false; // group header was clicked -> stay open
          return;
        }
        setOpen(false);
      }}
      options={options}
      loading={loading}
      value={selected}
      inputValue={inputValue}
      onInputChange={(_, v, why) => {
        // clicking a group header must not put its name in the box
        if (why !== "input" && options.some((o) => o.isGroup && o.Name === v)) return;
        setInputValue(v);
      }}
      onChange={(_, v) => {
        if (v && v.isGroup) {
          keepOpen.current = true;
          setExpanded((prev) => ({ ...prev, [v.Name]: !expanded[v.Name] }));
          return;
        }
        // after choosing, only the chosen reason's group stays open
        const g = v && groupOf ? groupOf(v.Code) : "";
        setExpanded(g ? { [g]: true } : {});
        onReason(v ? v.Code : "");
      }}
      filterOptions={filterOptions}
      getOptionLabel={(o) => o.Name || ""}
      isOptionEqualToValue={(a, b) => a.Code === b.Code}
      renderOption={(props, o) =>
        o.isGroup ? (
          <li {...props} key={o.Code} style={{ ...props.style, backgroundColor: "#eef2f7" }}>
            <Box sx={{ display: "flex", width: "100%", alignItems: "center", gap: 1 }}>
              {expanded[o.Name] ? (
                <ExpandMoreIcon fontSize="small" sx={{ color: NAVY }} />
              ) : (
                <ChevronRightIcon fontSize="small" sx={{ color: NAVY }} />
              )}
              <Typography component="span" sx={{ fontWeight: 700, color: NAVY, fontSize: "14px" }}>
                {o.Name}
              </Typography>
              <Typography component="span" sx={{ ml: "auto", fontSize: "11px", color: "#6b7280" }}>
                {o.count}
              </Typography>
            </Box>
          </li>
        ) : (
          <li {...props} key={o.Code} style={{ ...props.style, paddingLeft: o.inGroup && !searching ? 44 : undefined }}>
            <Box sx={{ width: "100%", minWidth: 0, whiteSpace: "normal", wordBreak: "break-word" }}>
              <span>{o.Name}</span>
              {o.inGroup && searching && <Typography sx={{ fontSize: "11px", color: "#6b7280" }}>{o.inGroup}</Typography>}
            </Box>
          </li>
        )
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label="Reason"
          placeholder="Select reason"
          inputProps={{ ...params.inputProps, "aria-label": `${title} reason` }}
          helperText={selectedGroup || undefined}
          FormHelperTextProps={{ sx: { mx: 0.5, color: NAVY, fontWeight: 600 } }}
          sx={changed ? changedSx : fieldSx}
        />
      )}
    />
  );
};

const changedSx = { "& .MuiOutlinedInput-root": { borderRadius: "12px", backgroundColor: "#fff8e6" } };

/* ---- One lost-time block (DTS or CDL): hours + reason + remark ---- */
const LostTimeBlock = ({
  title,
  color,
  hours,
  onHours,
  hoursError,
  reasons,
  reason,
  onReason,
  reasonsLoading,
  remark,
  onRemark,
  groupOf,
  changed = {},
}) => (
  <Box sx={{ p: 1.5, borderRadius: "12px", border: "1px solid #e3e8ef", backgroundColor: "#fafbfd" }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25 }}>
      <Box sx={{ width: 4, height: 16, borderRadius: 2, backgroundColor: color }} />
      <Typography sx={{ fontSize: "13px", fontWeight: 700, color: NAVY }}>{title}</Typography>
    </Box>
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "110px minmax(0, 1fr)", sm: "130px minmax(0, 1fr)" }, gap: 1.5 }}>
      <TextField
        type="number"
        label="Lost Hours"
        placeholder="0"
        value={hours}
        onChange={(e) => onHours(e.target.value)}
        inputProps={{ min: 0, step: 0.5, inputMode: "decimal", "aria-label": `${title} lost hours` }}
        error={!!hoursError}
        helperText={hoursError}
        sx={changed.hours ? changedSx : fieldSx}
      />
      <ReasonSelect
        title={title}
        reasons={reasons}
        reason={reason}
        onReason={onReason}
        loading={reasonsLoading}
        groupOf={groupOf}
        changed={changed.reason}
      />
      <TextField
        label="Remark"
        placeholder={`${title} delay remark`}
        value={remark}
        onChange={(e) => onRemark(e.target.value)}
        inputProps={{ "aria-label": `${title} remark` }}
        sx={{ ...(changed.remark ? changedSx : fieldSx), gridColumn: "1 / -1" }}
        fullWidth
      />
    </Box>
  </Box>
);

/* ---- empty form ---- */
const EMPTY_FORM = {
  commonRemark: "",
  lostHoursDts: "",
  lostHoursCdl: "",
  reasonDts: "",
  reasonCdl: "",
  remarkDts: "",
  remarkCdl: "",
};

// saved remark -> form values
const formFromRemark = (r) =>
  r
    ? {
        commonRemark: r.CommonRemark || "",
        lostHoursDts: hoursText(r.LostHoursDts),
        lostHoursCdl: hoursText(r.LostHoursCdl),
        reasonDts: r.ReasonDts || "",
        reasonCdl: r.ReasonCdl || "",
        remarkDts: r.RemarkDts || "",
        remarkCdl: r.RemarkCdl || "",
      }
    : EMPTY_FORM;

/* -------------------- Page -------------------- */
const ErectionDailyRemark = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const userId = localStorage.getItem("employeeId") || "";
  const formRef = useRef(null); // phones: jump to the stage details after a stage is chosen
  const datePickerRef = useRef(null);

  const { iwoList, iwoListLoading, iwoListError } = useSelector((state) => state.productionMonitor);
  const {
    units,
    unitsLoading,
    unitsError,
    unitFabrications,
    unitFabricationsLoading,
    unitFabricationsError,
    cdlDelayReasons,
    dtsDelayReasons,
    delayReasonsLoading,
    dailyRemarksKey,
    dailyRemarks,
    dailyRemarksLoading,
    dailyRemarksError,
    remarkSaving,
  } = useSelector((state) => state.erectionMonitor);

  const [iwo, setIwo] = useState(null);
  const [unit, setUnit] = useState(null);
  const [progressId, setProgressId] = useState(null);

  // ?iwoNo=&unitId=&progressId= when coming back from Remark History -> select them again once loaded
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const pending = useRef({
    iwoNo: params.get("iwoNo"),
    unitId: params.get("unitId"),
    progressId: params.get("progressId") ? Number(params.get("progressId")) : null,
  });
  const [remarkDate, setRemarkDate] = useState(formatDate(new Date()));
  const [editId, setEditId] = useState(null); // DailyId of the remark chosen from the list to edit
  const formCardRef = useRef(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [completion, setCompletion] = useState(0);
  const [markCompleted, setMarkCompleted] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [snack, setSnack] = useState({ open: false, severity: "success", message: "" });

  const set = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    dispatch(fetchPmIwoList());
    dispatch(fetchErUnits());
    dispatch(fetchErDelayReasons());
  }, [dispatch]);

  const iwoNo = iwo ? String(iwo.IwoNo) : "";
  const unitId = unit ? unit.UnitId : null;

  // stages of IWO + unit
  useEffect(() => {
    setProgressId(null);
    if (iwoNo && unitId) dispatch(fetchErUnitFabrications(iwoNo, unitId));
  }, [dispatch, iwoNo, unitId]);

  const activeUnits = useMemo(() => (units || []).filter((u) => u.IsActive !== false), [units]);

  useEffect(() => {
    const want = pending.current.iwoNo;
    if (!want || !(iwoList || []).length) return;
    const found = iwoList.find((x) => String(x.IwoNo) === String(want));
    if (found) setIwo(found);
    pending.current.iwoNo = null;
  }, [iwoList]);
  useEffect(() => {
    const want = pending.current.unitId;
    if (!want || !iwo || !activeUnits.length) return;
    const found = activeUnits.find((u) => String(u.UnitId) === String(want));
    if (found) setUnit(found);
    pending.current.unitId = null;
  }, [activeUnits, iwo]);

  // Remark History page (keeps IWO / unit / stage selected)
  const openHistory = () => {
    const q = new URLSearchParams();
    if (iwoNo) q.set("iwoNo", iwoNo);
    if (iwoNo && unitId) q.set("unitId", String(unitId));
    if (iwoNo && unitId && progressId) q.set("progressId", String(progressId));
    navigate(`/erectionRemarkHistory${q.toString() ? `?${q}` : ""}`);
  };

  // only use the answer for this IWO + unit
  const savedForThis =
    unitFabrications && String(unitFabrications.IwoNo) === iwoNo && String(unitFabrications.UnitId) === String(unitId)
      ? unitFabrications
      : null;

  // stages that have a ProgressId (target dates saved) in SeqNo order
  const allStages = useMemo(
    () => [...(savedForThis?.Fabrications || [])].sort((a, b) => Number(a.SeqNo) - Number(b.SeqNo)),
    [savedForThis]
  );
  const stages = useMemo(() => allStages.filter((s) => s.ProgressId), [allStages]);
  const stage = useMemo(() => stages.find((s) => s.ProgressId === progressId) || null, [stages, progressId]);

  // stage that came back from Remark History
  useEffect(() => {
    const want = pending.current.progressId;
    if (!want || !stages.length) return;
    if (stages.some((s) => s.ProgressId === want)) setProgressId(want);
    pending.current.progressId = null;
  }, [stages]);
  const noTargets = !!savedForThis && stages.length === 0; // target dates not inserted yet
  const doneCount = stages.filter(isStageDone).length;
  const status = targetStatus(stage);

  // remarks of the chosen stage
  useEffect(() => {
    if (progressId) dispatch(fetchErDailyRemarks(progressId));
  }, [dispatch, progressId]);
  const remarks = useMemo(
    () =>
      dailyRemarksKey === String(progressId)
        ? [...(dailyRemarks || [])].sort(
            (a, b) => toYmd(b.RemarkDate).localeCompare(toYmd(a.RemarkDate)) || Number(b.DailyId) - Number(a.DailyId)
          )
        : [],
    [dailyRemarksKey, dailyRemarks, progressId]
  );

  // Many remarks per stage per day are allowed (same or different users) -> Save always inserts a new one.
  // Update only when a remark is chosen from "Remarks of this stage" (Edit).
  const existing = useMemo(() => (editId ? remarks.find((r) => r.DailyId === editId) || null : null), [remarks, editId]);
  const isUpdate = !!existing;

  // another stage -> leave edit mode
  useEffect(() => {
    setEditId(null);
  }, [progressId]);

  const startEdit = (r) => {
    setEditId(r.DailyId);
    setRemarkDate(toYmd(r.RemarkDate));
    setTimeout(() => formCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };
  const cancelEdit = () => {
    setEditId(null);
    setRemarkDate(formatDate(new Date()));
  };

  // fill the form from the chosen remark; empty form for a new remark
  const formKey = `${progressId || ""}|${existing ? existing.DailyId : "new"}`;
  useEffect(() => {
    setForm(formFromRemark(existing));
    setShowErrors(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey]);

  /* ---- Physical completion (can only go up) ---- */
  const savedCompletion = Math.round(toNumber(stage?.PhysicalCompletion));
  const stageDone = isStageDone(stage);
  useEffect(() => {
    setCompletion(savedCompletion);
    setMarkCompleted(false);
  }, [progressId, savedCompletion]);

  const shownCompletion = markCompleted ? 100 : completion;
  const completionValid =
    markCompleted || (completion !== "" && !isNaN(completion) && completion >= savedCompletion && completion <= 100);
  const completionChanged = !stageDone && (markCompleted || (completionValid && Number(completion) !== savedCompletion));

  const chooseStage = (val) => {
    setProgressId(val ? val.ProgressId : null);
    if (val && isMobile) {
      setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
  };

  const openDatePicker = () => {
    const el = datePickerRef.current;
    if (!el) return;
    if (typeof el.showPicker === "function") el.showPicker();
    else el.click();
  };

  /* ---- what changed (update) ---- */
  const base = formFromRemark(existing);
  const fieldChanged = (k) => {
    if (!isUpdate) return false;
    if (k === "lostHoursDts" || k === "lostHoursCdl") return toNumber(form[k]) !== toNumber(base[k]);
    return String(form[k]).trim() !== String(base[k]).trim();
  };

  const updatePayload = () => {
    const p = {};
    const map = {
      commonRemark: "commonRemark",
      remarkDts: "remarkDts",
      remarkCdl: "remarkCdl",
      reasonDts: "reasonDts",
      reasonCdl: "reasonCdl",
    };
    Object.keys(map).forEach((k) => {
      if (fieldChanged(k)) p[map[k]] = String(form[k]).trim();
    });
    if (fieldChanged("lostHoursDts")) p.lostHoursDts = toNumber(form.lostHoursDts);
    if (fieldChanged("lostHoursCdl")) p.lostHoursCdl = toNumber(form.lostHoursCdl);
    if (completionChanged) {
      if (markCompleted) p.completionStatus = true; // physical completion goes to 100 automatically
      else p.physicalCompletion = Math.round(Number(completion));
    }
    return p;
  };
  const changes = isUpdate ? updatePayload() : {};
  const changeCount = Object.keys(changes).length;

  /* ---- Validation ---- */
  const errors = {
    stage: !stage ? "Select a stage" : "",
    remarkDate: !isValidYmd(remarkDate) ? "Enter remark date as yyyy-mm-dd" : "",
    lostHoursDts: toNumber(form.lostHoursDts) < 0 ? "Cannot be negative" : "",
    lostHoursCdl: toNumber(form.lostHoursCdl) < 0 ? "Cannot be negative" : "",
    completion: !stageDone && !completionValid ? "Invalid completion" : "",
  };
  const hasErrors = Object.values(errors).some(Boolean);
  const err = (k) => (showErrors ? errors[k] : "");

  /* ---- Save ---- */
  const handleSave = async () => {
    if (hasErrors) {
      setShowErrors(true);
      return;
    }
    if (isUpdate && changeCount === 0) return;

    let payload;
    if (isUpdate) {
      // dailyId tells which remark of that date is updated (there can be many on one day)
      payload = { dailyId: String(existing.DailyId), progressId: String(stage.ProgressId), remarkDate, ...changes, updatedBy: userId };
    } else {
      payload = {
        progressId: String(stage.ProgressId),
        remarkDate,
        commonRemark: form.commonRemark.trim(),
        lostHoursCdl: form.lostHoursCdl === "" ? undefined : toNumber(form.lostHoursCdl),
        lostHoursDts: form.lostHoursDts === "" ? undefined : toNumber(form.lostHoursDts),
        reasonCdl: form.reasonCdl, // code, e.g. "CD"
        reasonDts: form.reasonDts, // code, e.g. "EA"
        remarkCdl: form.remarkCdl.trim(),
        remarkDts: form.remarkDts.trim(),
        createdBy: userId,
      };
      // completed stage: leave completion as it is
      if (!stageDone) {
        payload.completionStatus = markCompleted; // true -> physical completion set to 100 automatically
        // only when moved up (another user may have saved a higher value meanwhile)
        if (!markCompleted && completionChanged) payload.physicalCompletion = Math.round(Number(completion));
      }
    }

    const result = await dispatch(saveErDailyRemark({ isUpdate, payload }));

    if (result.success) {
      setSnack({
        open: true,
        severity: "success",
        message: `Remark ${isUpdate ? "updated" : "saved"} for ${stage.TypeName} (${remarkDate})`,
      });
      setShowErrors(false);
      if (isUpdate) setEditId(null); // back to adding a new remark
      setForm(EMPTY_FORM);
      setMarkCompleted(false);
      dispatch(fetchErDailyRemarks(stage.ProgressId)); // refresh remarks
      dispatch(fetchErUnitFabrications(iwoNo, unitId)); // refresh completion
    } else {
      setSnack({ open: true, severity: "error", message: result.message });
    }
  };

  const reasonName = (list, code) => (list || []).find((r) => r.Code === code)?.Name || code || "";
  const saveDisabled = remarkSaving || (isUpdate && changeCount === 0);

  return (
    <Box sx={{ width: "100%", minHeight: "100vh", display: "flex", flexDirection: "column", backgroundColor: "#f5f7fb" }}>
      <HeaderBannerPage />

      <Box
        sx={{
          maxWidth: "1200px",
          width: "100%",
          mx: "auto",
          px: { xs: 1.5, sm: 2, md: 4 },
          py: { xs: 2, md: 3 },
          boxSizing: "border-box",
          fontFamily: "'Poppins', sans-serif",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, maxWidth: 760, mx: "auto" }}>
          <EditNoteIcon sx={{ color: BLUE }} />
          <Typography sx={{ fontSize: { xs: "19px", md: "22px" }, fontWeight: 700, color: "#0E3C66", letterSpacing: "0.5px" }}>
            Update Daily Remark
          </Typography>
          <Chip size="small" label="Erection" sx={{ ml: 0.5, backgroundColor: "#e8f0fa", color: "#0E3C66", fontWeight: 600 }} />
          {/* Remark History page */}
          <Button
            variant="outlined"
            size="small"
            startIcon={<HistoryIcon />}
            onClick={openHistory}
            aria-label="Remark history"
            sx={{
              ml: "auto",
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "10px",
              color: BLUE,
              borderColor: "#c9d6e6",
              backgroundColor: "#fff",
              minWidth: 0,
              px: { xs: 1, sm: 1.5 },
              "& .MuiButton-startIcon": { mr: { xs: 0, sm: 0.75 } },
            }}
          >
            <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
              History
            </Box>
          </Button>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 760, mx: "auto" }}>
          {/* IWO -> Unit -> Stage */}
          <Paper elevation={0} sx={cardSx}>
            {/* Step 1: IWO + Eng Spec */}
            <Autocomplete
              options={iwoList || []}
              value={iwo}
              onChange={(_, v) => {
                setIwo(v);
                setUnit(null);
                setProgressId(null);
              }}
              loading={iwoListLoading}
              autoHighlight
              getOptionLabel={(o) => (o ? `${o.IwoNo}${o.EngSpec ? ` - ${o.EngSpec}` : ""}` : "")}
              isOptionEqualToValue={(o, v) => String(o.IwoNo) === String(v.IwoNo)}
              filterOptions={(opts, { inputValue }) => {
                const q = inputValue.trim().toLowerCase();
                if (!q) return opts;
                return opts.filter((o) =>
                  [o.IwoNo, o.EngSpec, o.AdditionalDescription].some((v) => String(v || "").toLowerCase().includes(q))
                );
              }}
              renderOption={(props, o) => (
                <Box component="li" {...props} key={o.IwoNo} sx={{ display: "block !important", py: "8px !important" }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#0E3C66" }}>{o.IwoNo}</Typography>
                  {o.EngSpec && (
                    <Typography sx={{ fontSize: "12px", color: "#555", whiteSpace: "normal", wordBreak: "break-word" }}>{o.EngSpec}</Typography>
                  )}
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="IWO No - Eng Spec"
                  placeholder="Search IWO no or eng spec"
                  error={!!iwoListError || (!!err("stage") && !iwo)}
                  helperText={iwoListError || (!iwo ? err("stage") && "Select an IWO" : iwo?.EngSpec || "")}
                  sx={fieldSx}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {iwoListLoading ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            {/* Step 2: unit */}
            <Autocomplete
              sx={{ mt: 2 }}
              options={activeUnits}
              value={unit}
              onChange={(_, v) => setUnit(v)}
              disabled={!iwo}
              loading={unitsLoading}
              autoHighlight
              getOptionLabel={(u) => (u ? u.UnitName || "" : "")}
              isOptionEqualToValue={(o, v) => o.UnitId === v.UnitId}
              noOptionsText="No units"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Unit"
                  placeholder={iwo ? "Select unit" : "Select an IWO first"}
                  error={!!unitsError || (!!iwo && !unit && !!err("stage"))}
                  helperText={unitsError || (iwo && !unit && err("stage") ? "Select a unit" : "")}
                  sx={fieldSx}
                />
              )}
            />

            {/* Step 3: stage (unit fabrication) */}
            <Autocomplete
              sx={{ mt: 2 }}
              options={stages}
              value={stage}
              onChange={(_, val) => chooseStage(val)}
              disabled={!iwo || !unit || noTargets}
              loading={unitFabricationsLoading}
              autoHighlight
              getOptionLabel={(s) => (s ? `${s.SeqNo}. ${s.TypeName}` : "")}
              isOptionEqualToValue={(o, v) => o.ProgressId === v.ProgressId}
              noOptionsText="No stages for this unit"
              renderOption={(props, s) => {
                const st = targetStatus(s);
                return (
                  <Box component="li" {...props} key={s.ProgressId} sx={{ display: "block !important", py: "8px !important" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#0E3C66", whiteSpace: "normal" }}>
                        {s.SeqNo}. {s.TypeName}
                      </Typography>
                      {isStageDone(s) && <Chip label="Completed" size="small" color="success" sx={{ height: 20, fontSize: "11px" }} />}
                      {st && (
                        <Chip
                          label={st.label}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: "11px",
                            fontWeight: 700,
                            backgroundColor: END_COLORS[st.level].chip,
                            color: END_COLORS[st.level].chipText,
                          }}
                        />
                      )}
                    </Box>
                    <Typography sx={{ fontSize: "12px", color: "#6b7280" }}>
                      Target {toYmd(s.TargetDate) || "—"} · {Math.round(toNumber(s.PhysicalCompletion))}% done
                      {s.ContributePercentage !== undefined && s.ContributePercentage !== null ? ` · ${pct(s.ContributePercentage)} of unit` : ""}
                    </Typography>
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Stage"
                  placeholder={!iwo ? "Select an IWO first" : !unit ? "Select a unit first" : "Select stage"}
                  error={!!unitFabricationsError || (!!unit && !stage && !!err("stage"))}
                  helperText={
                    unitFabricationsError ||
                    (noTargets
                      ? "Insert target dates for this unit first"
                      : unit && !stage && err("stage")
                      ? "Select a stage"
                      : unit && stages.length
                      ? `${stages.length} stage(s) · ${doneCount} completed`
                      : "")
                  }
                  FormHelperTextProps={{ sx: { color: noTargets ? "#c62828" : undefined } }}
                  sx={fieldSx}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {unitFabricationsLoading ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Paper>

          {/* Stage details + physical completion (before the remark fields) */}
          {stage && (
            <Paper elevation={0} sx={{ ...cardSx, scrollMarginTop: "72px" }} ref={formRef}>
              {/* target date alert: yellow the day before, red on / after the target date */}
              {status && (
                <Box
                  role="alert"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1.5,
                    px: 1.5,
                    py: 1.1,
                    borderRadius: "12px",
                    border: `2px solid ${END_COLORS[status.level].border}`,
                    backgroundColor: END_COLORS[status.level].bg,
                    color: END_COLORS[status.level].text,
                    animation: "endPulse 1.6s ease-in-out infinite",
                    "@keyframes endPulse": {
                      "0%, 100%": { boxShadow: `0 0 0 0 ${END_COLORS[status.level].border}66` },
                      "50%": { boxShadow: `0 0 0 6px ${END_COLORS[status.level].border}00` },
                    },
                    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
                  }}
                >
                  <Box component="span" sx={{ fontSize: "20px", lineHeight: 1 }}>{status.level === "due" ? "⏰" : "⚠️"}</Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: "14px", fontWeight: 800, letterSpacing: "0.3px" }}>{status.title.toUpperCase()}</Typography>
                    <Typography sx={{ fontSize: "12px", fontWeight: 500 }}>{status.text}</Typography>
                  </Box>
                </Box>
              )}

              <Box sx={{ p: 1.5, borderRadius: "12px", backgroundColor: "#f3f6fa" }}>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" }, gap: 1.5 }}>
                  <InfoItem label="IWO" value={savedForThis?.IwoNo} />
                  <InfoItem label="Unit" value={savedForThis?.UnitName || unit?.UnitName} />
                  <InfoItem label="Contribution" value={stage.ContributePercentage !== undefined && stage.ContributePercentage !== null ? pct(stage.ContributePercentage) : ""} />
                  {/* Target date coloured when it is close / due */}
                  <Box
                    sx={
                      status
                        ? {
                            mx: -0.75,
                            my: -0.5,
                            px: 0.75,
                            py: 0.5,
                            borderRadius: "8px",
                            backgroundColor: END_COLORS[status.level].chip,
                            "& p": { color: `${END_COLORS[status.level].chipText} !important` },
                          }
                        : undefined
                    }
                  >
                    <InfoItem label="Target" value={toYmd(stage.TargetDate)} />
                  </Box>
                </Box>
                <Box sx={{ mt: 1.5 }}>
                  <InfoItem label="Stage" value={`${stage.SeqNo}. ${stage.TypeName}`} />
                </Box>

                {/* Physical completion (saved with the remark) */}
                <Box sx={{ mt: 2, pt: 1.5, borderTop: "1px dashed #d6dce5" }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography sx={{ fontSize: "11px", color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Physical Completion
                    </Typography>
                    <Typography sx={{ fontSize: "12px", color: "#6b7280" }}>
                      Saved: <b style={{ color: NAVY }}>{savedCompletion}%</b>
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, Math.max(0, savedCompletion))}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      mb: 1,
                      backgroundColor: "#e1e7ef",
                      "& .MuiLinearProgress-bar": { backgroundColor: stageDone ? "#2e7d32" : BLUE },
                    }}
                  />
                  {stageDone ? (
                    <Chip label="Stage completed" color="success" size="small" sx={{ fontWeight: 700 }} />
                  ) : (
                    <>
                      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 96px", sm: "1fr 96px" }, gap: 1.5, alignItems: "center" }}>
                        <Slider
                          value={completionValid ? Number(shownCompletion) : 0}
                          onChange={(_, v) => setCompletion(Math.max(savedCompletion, v))}
                          step={5}
                          min={0}
                          max={100}
                          valueLabelDisplay="auto"
                          valueLabelFormat={(v) => `${v}%`}
                          disabled={markCompleted}
                          sx={{ color: BLUE, mx: 1, width: "auto" }}
                        />
                        <TextField
                          size="small"
                          type="number"
                          value={shownCompletion}
                          disabled={markCompleted}
                          onChange={(e) => setCompletion(e.target.value === "" ? "" : Number(e.target.value))}
                          onBlur={() => {
                            // typed a lower value -> bring it back to the saved value
                            if (completion === "" || isNaN(completion) || completion < savedCompletion) setCompletion(savedCompletion);
                            else if (completion > 100) setCompletion(100);
                          }}
                          inputProps={{ min: savedCompletion, max: 100, step: 1, inputMode: "numeric", "aria-label": "Physical completion %" }}
                          InputProps={{ endAdornment: <Typography sx={{ fontSize: "13px", color: "#6b7280" }}>%</Typography> }}
                          error={!completionValid}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "10px",
                              backgroundColor: completionChanged && !markCompleted ? "#fff8e6" : "#fff",
                            },
                          }}
                        />
                      </Box>
                      {!completionValid && (
                        <Typography sx={{ fontSize: "11px", color: "#c62828", mt: 0.5 }}>
                          {Number(completion) < savedCompletion
                            ? `Can't be lower than the saved value (${savedCompletion}%)`
                            : `Enter a value from ${savedCompletion} to 100`}
                        </Typography>
                      )}
                      <FormControlLabel
                        sx={{ mt: 0.5 }}
                        control={
                          <Checkbox
                            checked={markCompleted}
                            onChange={(e) => setMarkCompleted(e.target.checked)}
                            sx={{ color: "#2e7d32", "&.Mui-checked": { color: "#2e7d32" } }}
                          />
                        }
                        label={
                          <Typography sx={{ fontSize: "13px", fontWeight: 600, color: markCompleted ? "#2e7d32" : NAVY }}>
                            Mark this stage as completed (100%)
                          </Typography>
                        }
                      />
                    </>
                  )}
                </Box>
              </Box>
            </Paper>
          )}

          {/* Form */}
          <Paper
            elevation={0}
            ref={formCardRef}
            sx={{ ...cardSx, scrollMarginTop: "72px", ...(isUpdate ? { border: `2px solid ${BLUE}` } : {}) }}
          >
            <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 2 }}>
              {isUpdate && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.5,
                    py: 1,
                    borderRadius: "10px",
                    backgroundColor: "#eef4fb",
                    border: "1px solid #c9d6e6",
                  }}
                >
                  <EditNoteIcon sx={{ color: BLUE }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: "13px", fontWeight: 700, color: NAVY }}>Editing remark of {toYmd(existing.RemarkDate)}</Typography>
                    <Typography sx={{ fontSize: "11px", color: "#6b7280" }}>By {existing.CreatedBy || "—"}</Typography>
                  </Box>
                  <Button size="small" onClick={cancelEdit} sx={{ ml: "auto", textTransform: "none", fontWeight: 700 }}>
                    Cancel edit
                  </Button>
                </Box>
              )}
              <Box sx={{ position: "relative" }}>
                <TextField
                  label="Remark Date"
                  placeholder="yyyy-mm-dd"
                  value={remarkDate}
                  onChange={(e) => setRemarkDate(formatTypedDate(e.target.value))}
                  disabled={isUpdate} // the date of a saved remark is not changed
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ inputMode: "numeric", maxLength: 10 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton edge="end" size="small" onClick={openDatePicker} disabled={isUpdate} aria-label="Pick remark date">
                          <CalendarMonthIcon sx={{ color: BLUE }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  error={!!err("remarkDate")}
                  helperText={err("remarkDate")}
                  sx={fieldSx}
                  fullWidth
                />
                {/* Hidden native picker - only used to open the calendar; value stays yyyy-mm-dd */}
                <input
                  ref={datePickerRef}
                  type="date"
                  tabIndex={-1}
                  aria-hidden="true"
                  value={isValidYmd(remarkDate) ? remarkDate : ""}
                  onChange={(e) => e.target.value && setRemarkDate(e.target.value)}
                  style={{ position: "absolute", right: 12, bottom: 0, width: 1, height: 1, opacity: 0, border: 0, padding: 0, pointerEvents: "none" }}
                />
              </Box>

              <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 1.5 }}>
                <LostTimeBlock
                  title="DTS"
                  color={BLUE}
                  hours={form.lostHoursDts}
                  onHours={set("lostHoursDts")}
                  hoursError={err("lostHoursDts")}
                  reasons={dtsDelayReasons || []}
                  reason={form.reasonDts}
                  onReason={set("reasonDts")}
                  reasonsLoading={delayReasonsLoading}
                  remark={form.remarkDts}
                  onRemark={set("remarkDts")}
                  changed={{ hours: fieldChanged("lostHoursDts"), reason: fieldChanged("reasonDts"), remark: fieldChanged("remarkDts") }}
                />
                <LostTimeBlock
                  title="CDL"
                  color="#e0a100"
                  hours={form.lostHoursCdl}
                  onHours={set("lostHoursCdl")}
                  hoursError={err("lostHoursCdl")}
                  reasons={cdlDelayReasons || []}
                  groupOf={cdlGroupOf}
                  reason={form.reasonCdl}
                  onReason={set("reasonCdl")}
                  reasonsLoading={delayReasonsLoading}
                  remark={form.remarkCdl}
                  onRemark={set("remarkCdl")}
                  changed={{ hours: fieldChanged("lostHoursCdl"), reason: fieldChanged("reasonCdl"), remark: fieldChanged("remarkCdl") }}
                />
              </Box>

              <TextField
                label="Remark"
                placeholder="What happened today on this stage?"
                value={form.commonRemark}
                onChange={(e) => set("commonRemark")(e.target.value)}
                multiline
                rows={3}
                sx={fieldChanged("commonRemark") ? changedSx : fieldSx}
                fullWidth
              />

              <Box
                sx={{
                  // phones: keep Save reachable at the bottom while typing
                  position: { xs: "sticky", md: "static" },
                  bottom: 0,
                  zIndex: 5,
                  mx: { xs: -1.5, md: 0 },
                  mb: { xs: -1.5, md: 0 },
                  p: { xs: 1.5, md: 0 },
                  backgroundColor: { xs: "rgba(255,255,255,0.97)", md: "transparent" },
                  borderBottomLeftRadius: "16px",
                  borderBottomRightRadius: "16px",
                }}
              >
                {isUpdate && (
                  <Typography sx={{ fontSize: "12px", color: "#6b7280", mb: 0.75, textAlign: "center" }}>
                    {changeCount ? `${changeCount} change(s)` : "No changes"}
                  </Typography>
                )}
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={remarkSaving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                  disabled={saveDisabled}
                  onClick={handleSave}
                  sx={{
                    height: 50,
                    borderRadius: "12px",
                    backgroundColor: isUpdate ? NAVY : "#2e7d32",
                    fontWeight: 700,
                    fontSize: "15px",
                    textTransform: "none",
                    "&:hover": { backgroundColor: isUpdate ? "#0D47A1" : "#1b5e20" },
                    "&:disabled": { backgroundColor: "#cccccc", color: "#666666" },
                  }}
                >
                  {isUpdate ? "Update Remark" : "Save Remark"}
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* Remarks of this stage (Edit -> update that remark) */}
          {stage && (
            <Paper elevation={0} sx={cardSx}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "#0E3C66" }}>Remarks of this stage</Typography>
                <Chip size="small" label={remarks.length} sx={{ height: 20, fontSize: "11px", fontWeight: 700 }} />
                {dailyRemarksLoading && <CircularProgress size={16} sx={{ ml: "auto" }} />}
              </Box>

              {dailyRemarksError ? (
                <Alert severity="error" sx={{ borderRadius: "10px" }}>{dailyRemarksError}</Alert>
              ) : remarks.length === 0 ? (
                <Typography sx={{ fontSize: "13px", color: "#9aa3b2", py: 2, textAlign: "center" }}>
                  {dailyRemarksLoading ? "Loading…" : "No remarks yet"}
                </Typography>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {remarks.map((r) => {
                    const active = r.DailyId === editId;
                    return (
                      <Box
                        key={r.DailyId}
                        sx={{
                          p: 1.25,
                          borderRadius: "12px",
                          border: `1px solid ${active ? BLUE : "#e3e8ef"}`,
                          backgroundColor: active ? "#eef4fb" : "#fafbfd",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                          <Typography sx={{ fontSize: "13px", fontWeight: 700, color: NAVY }}>{toYmd(r.RemarkDate)}</Typography>
                          {active && <Chip size="small" label="Editing" sx={{ height: 18, fontSize: "10px", backgroundColor: BLUE, color: "#fff" }} />}
                          <Typography sx={{ fontSize: "11px", color: "#6b7280" }}>
                            By {r.CreatedBy || "—"}
                            {r.CreatedDate ? ` · ${String(r.CreatedDate).slice(11, 16)}` : ""}
                            {r.UpdatedBy ? ` · edited by ${r.UpdatedBy}` : ""}
                          </Typography>
                          <Button
                            size="small"
                            variant={active ? "contained" : "outlined"}
                            startIcon={<EditNoteIcon />}
                            onClick={() => (active ? cancelEdit() : startEdit(r))}
                            aria-label={`Edit remark ${r.DailyId}`}
                            sx={{
                              ml: "auto",
                              height: 26,
                              textTransform: "none",
                              fontWeight: 700,
                              borderRadius: "8px",
                              ...(active ? { backgroundColor: BLUE } : { color: BLUE, borderColor: "#c9d6e6" }),
                            }}
                          >
                            {active ? "Editing" : "Edit"}
                          </Button>
                        </Box>
                        {r.CommonRemark && (
                          <Typography sx={{ fontSize: "13px", color: "#333", mt: 0.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                            {r.CommonRemark}
                          </Typography>
                        )}
                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 0.75, mt: 0.75 }}>
                          {[
                            { t: "DTS", c: BLUE, h: r.LostHoursDts, reason: reasonName(dtsDelayReasons, r.ReasonDts), remark: r.RemarkDts },
                            { t: "CDL", c: "#e0a100", h: r.LostHoursCdl, reason: reasonName(cdlDelayReasons, r.ReasonCdl), remark: r.RemarkCdl },
                          ]
                            .filter((x) => Number(x.h) > 0 || x.reason || x.remark)
                            .map((x) => (
                              <Box key={x.t} sx={{ pl: 1, borderLeft: `3px solid ${x.c}` }}>
                                <Typography sx={{ fontSize: "12px", fontWeight: 700, color: NAVY }}>
                                  {x.t}
                                  {Number(x.h) > 0 ? ` · ${Number(x.h)} h lost` : ""}
                                </Typography>
                                {x.reason && <Typography sx={{ fontSize: "12px", color: "#444" }}>{x.reason}</Typography>}
                                {x.remark && <Typography sx={{ fontSize: "12px", color: "#6b7280", wordBreak: "break-word" }}>{x.remark}</Typography>}
                              </Box>
                            ))}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Paper>
          )}
        </Box>
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ErectionDailyRemark;